import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { track, getAttribution } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Farmhouses", "Resorts", "Banquet Halls", "Function Halls"];
const empty = { name: "", phone: "", email: "", category: "", location: "", date: "", guests: "", budget: "", message: "" };

export const LeadForm = ({ compact }) => {
  const [f, setF] = useState(empty);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) return toast.error("Please enter your name and mobile number.");
    try {
      await api.post("/leads", { ...f, guests: Number(f.guests) || 0, attribution: getAttribution() });
      track("lead_created", { category: f.category });
      setSent(true);
      setF(empty);
      toast.success("Thanks! Our team will reach out to help you find the perfect venue.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (sent) {
    return (
      <div data-testid="lead-success" className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
        <p className="font-heading text-lg font-semibold text-green-800">Request received 🎉</p>
        <p className="mt-1 text-sm text-green-700">Our team will contact you shortly to help you find and book the right venue.</p>
        <Button variant="outline" className="mt-4 rounded-full" onClick={() => setSent(false)}>Send another request</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div><Label className="text-xs text-stone-500">Name *</Label><Input data-testid="lead-name" value={f.name} onChange={set("name")} className="mt-1 rounded-xl" placeholder="Your name" /></div>
        <div><Label className="text-xs text-stone-500">Mobile *</Label><Input data-testid="lead-phone" value={f.phone} onChange={set("phone")} className="mt-1 rounded-xl" placeholder="10-digit mobile" /></div>
        {!compact && <div><Label className="text-xs text-stone-500">Email</Label><Input data-testid="lead-email" value={f.email} onChange={set("email")} className="mt-1 rounded-xl" placeholder="you@email.com" /></div>}
        <div>
          <Label className="text-xs text-stone-500">Category</Label>
          <Select value={f.category} onValueChange={set("category")}>
            <SelectTrigger data-testid="lead-category" className="mt-1 rounded-xl"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs text-stone-500">Location</Label><Input data-testid="lead-location" value={f.location} onChange={set("location")} className="mt-1 rounded-xl" placeholder="e.g. Moinabad" /></div>
        {!compact && <div><Label className="text-xs text-stone-500">Guests</Label><Input data-testid="lead-guests" value={f.guests} onChange={set("guests")} type="number" className="mt-1 rounded-xl" placeholder="e.g. 50" /></div>}
        {!compact && <div><Label className="text-xs text-stone-500">Budget (₹)</Label><Input data-testid="lead-budget" value={f.budget} onChange={set("budget")} className="mt-1 rounded-xl" placeholder="e.g. 25000" /></div>}
      </div>
      {!compact && <div><Label className="text-xs text-stone-500">Message</Label><Textarea data-testid="lead-message" value={f.message} onChange={set("message")} className="mt-1 rounded-xl" placeholder="Tell us about your event or stay..." /></div>}
      <Button data-testid="lead-submit" type="submit" className="w-full rounded-xl bg-primary py-6 text-base hover:bg-green-900">Request a call back</Button>
    </form>
  );
};
